import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { StyledMap, StyledContentMap, StyledMapList, StyledBackBtn } from './styled';
import MapHelper from './map-helper/map-helper';
import { StoreUl } from './components';
import { fitBounds } from './utils/fitBounds';
import { IM82FindDealerMapBlockViewModel } from './models';
import markerSelected from '$assets/pinimerco_black.svg';
import markerOption from '$assets/pinimerco.svg';
import { generateMapMarkers } from './utils/generateMapMarkers';
import { Store } from '~/lib/data-contract/salesforce';
import { useCheckoutState } from '~/features/checkout/hooks/useCheckoutState';
import ChevronLeft from '~/icons/chevron-left.svg';
import IndividualStoreSchema from './IndividualStoreSchema';

type Props = {
    store: Store;
};

type ComponentProps = IM82FindDealerMapBlockViewModel & Props;

export const StoreMap = ({ store }: ComponentProps) => {
    const mapMarkers = useMemo(() => generateMapMarkers([store]), [store]);

    const [map, setMap] = useState<google.maps.Map | null>(null);
    const { deliveryAddress: selectedPin, setDeliveryAddress } = useCheckoutState();

    const router = useRouter();
    const handleBack = () => router.back();

    const selectOptionStore = (storeOption: any) => {
        const clickLocal = {
            lat: storeOption.latitude || storeOption.lat,
            lng: storeOption.longitude || storeOption.lng,
        };

        if (map) {
            map.setZoom(15);
            map.panTo(clickLocal);
        }

        setDeliveryAddress(storeOption);
    };

    /**
     * When locations are updated,
     * fit bounds to the new markers
     */
    useEffect(() => {
        if (map && mapMarkers.length > 0) {
            fitBounds(map, mapMarkers, 1);
            setTimeout(() => {
                map.setZoom(15);
            }, 100);
        }
    }, [map]);

    /**
     * When map is loaded, set it in state,
     * and if mapmarkers already are theye
     */
    const mapLoaded = useCallback((map) => {
        setMap(map);
    }, []);

    return (
        <StyledContentMap
            aria-label={`Dette er en side med butikoplysninger vedrørende ${store?.name}`}
        >
            <IndividualStoreSchema storeData={mapMarkers} />
            <StyledMapList>
                <StyledBackBtn>
                    <ChevronLeft
                        onClick={handleBack}
                        tabIndex={0}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleBack();
                            }
                        }}
                    />
                </StyledBackBtn>
                <StoreUl storeList={mapMarkers} selectedPin={selectedPin} />
            </StyledMapList>
            <StyledMap data-testid="maps">
                <MapHelper
                    markers={mapMarkers}
                    markerIcon={markerOption}
                    selectedMarkerIcon={markerSelected}
                    onMapLoadCallback={mapLoaded}
                    onPinClick={selectOptionStore}
                    clusterOptions={{
                        maxZoom: 12,
                        clusterClass: 'mapHelper__cluster',
                        averageCenter: true,
                        styles: [
                            {
                                textColor: '#000',
                                fontFamily: 'inherit',
                                url: '',
                                height: 40,
                                width: 40,
                            },
                        ],
                    }}
                    selectedPin={selectedPin}
                />
            </StyledMap>
        </StyledContentMap>
    );
};
